/**
 * admin/src/hooks/useUsers.ts
 * Hooks cho User management page
 *
 * useUsers    — danh sách có server-side filter/paginate/sort
 * useUser     — 1 user detail (profile + students + subs)
 * useUserMut  — mutation: đổi tier
 */

import { useState, useEffect, useCallback } from 'react';
import { adminFetch } from './useAdmin';
import { toast } from '../components/ui/index';
import type {
  AdminUserRow,
  AdminUserDetail,
  PaginatedResponse,
  SubscriptionTier,
} from '../types/admin';

// ─── Params cho danh sách ────────────────────────────────────────────────────
export interface UsersParams {
  page?: number;
  limit?: number;
  search?: string;
  tier?: SubscriptionTier | '';
  sort?: string;
  dir?: 'asc' | 'desc';
}

interface UseUsersReturn {
  users: AdminUserRow[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useUsers(params: UsersParams = {}): UseUsersReturn {
  const [data, setData] = useState<PaginatedResponse<AdminUserRow> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<PaginatedResponse<AdminUserRow>>('/users', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        search: params.search || undefined,
        tier: params.tier || undefined,
        sort: params.sort || 'created_at',
        dir: params.dir || 'desc',
      },
    })
      .then((res) => { if (!cancelled) { setData(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải users');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), tick]);

  return {
    users: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 1,
    isLoading,
    error,
    refetch,
  };
}

// ─── User Detail ──────────────────────────────────────────────────────────────
interface UseUserReturn {
  user: AdminUserDetail | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useUser(userId: string | undefined): UseUserReturn {
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!userId) { setIsLoading(false); return; }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<AdminUserDetail>(`/users/${userId}`)
      .then((res) => { if (!cancelled) { setUser(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải user');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [userId, tick]);

  return { user, isLoading, error, refetch };
}

// ─── Mutations ────────────────────────────────────────────────────────────────
export function useUserMutations(onSuccess?: () => void) {
  const [isSaving, setIsSaving] = useState(false);

  /** Upgrade user lên Premium: tạo subscription với thời hạn */
  async function setPremium(userId: string, days: number) {
    setIsSaving(true);
    try {
      await adminFetch(`/subscriptions`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, plan: 'premium', days }),
      });
      toast(`Đã set Premium (${days} ngày)`, 'success');
      onSuccess?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi set Premium', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  /** Downgrade user về Member: expire tất cả active subscriptions */
  async function setMember(userId: string) {
    setIsSaving(true);
    try {
      await adminFetch(`/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ tier: 'member' }),
      });
      toast('Đã đổi về Member', 'success');
      onSuccess?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi đổi tier', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  return { setPremium, setMember, isSaving };
}
